import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { useTheme } from '@/context/ThemeContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import UserMenu from '@/components/common/UserMenu'
import MobileBottomNav from '@/components/common/MobileBottomNav'
import RestaurantSwitcher from '@/components/common/RestaurantSwitcher'
import {
  BarChart3,
  TrendingUp,
  FileText,
  DollarSign,
  XCircle,
  Receipt,
  CreditCard,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Menu,
  X,
  Smartphone,
  Package,
  Clock,
  ChevronDown,
  ChevronRight,
  Warehouse,
  ShoppingCart,
  ChefHat,
  LayoutGrid,
  ClipboardList,
  Users,
  PieChart,
  Pencil,
  Trophy,
  Wallet,
  Percent,
  Tag,
  UserCheck,
  Grid3x3,
  ShieldAlert,
  Landmark,
  Bike,
  ScrollText
} from 'lucide-react'
import logoDark from '@/assets/own-lg.png'
import logoLight from '@/assets/own-lg-light.png'
import logoSvg from '@/assets/logo.svg'
import { cn } from '@/lib/utils'
import { getDaysRemaining } from '@/lib/utils'
import { isFeatureEnabled } from '@/lib/features'

interface RestaurantLayoutProps {
  children: React.ReactNode
}

interface NavChild {
  feature: string
  name: string
  href: string
  icon: any
}

interface NavItem {
  feature?: string
  name: string
  href?: string
  icon: any
  children?: NavChild[]
  isNew?: boolean
}

const restaurantNavigation: NavItem[] = [
  { feature: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
  { feature: 'analytics', name: 'Analytics', href: '/analytics', icon: TrendingUp },
  { feature: 'customers', name: 'Customers', href: '/customers', icon: Users, isNew: true },
  { feature: 'category-analysis', name: 'Category Analysis', href: '/category-analysis', icon: LayoutGrid },
  { feature: 'sales-reports', name: 'Sales Reports', href: '/sales-reports', icon: FileText },
  { feature: 'online-orders', name: 'Online Orders', href: '/online-orders', icon: Smartphone },
  { feature: 'parcel-takeaway', name: 'Parcel & Takeaway', href: '/parcel-takeaway', icon: Package },
  { feature: 'profit-analysis', name: 'Profit Analysis', href: '/profit-analysis', icon: DollarSign },
  { feature: 'hourly-distribution', name: 'Hourly Distribution', href: '/hourly-distribution', icon: Clock },
  { feature: 'cancellations', name: 'Cancellations', href: '/cancellations', icon: XCircle },
  { feature: 'edited-bills', name: 'Edited Bills', href: '/edited-bills', icon: Pencil },
  { feature: 'menu-performance', name: 'Menu Performance', href: '/menu-performance', icon: Trophy },
  { feature: 'payment-modes', name: 'Payment Modes', href: '/payment-modes', icon: Wallet },
  { feature: 'gst-report', name: 'GST Report', href: '/gst-report', icon: Percent },
  { feature: 'discounts', name: 'Discounts & Round-off', href: '/discounts', icon: Tag },
  { feature: 'staff-performance', name: 'Staff Performance', href: '/staff-performance', icon: UserCheck },
  { feature: 'table-performance', name: 'Table & Area', href: '/table-performance', icon: Grid3x3 },
  { feature: 'audit-trail', name: 'Audit Trail', href: '/audit-trail', icon: ShieldAlert },
  { feature: 'receivables', name: 'Receivables', href: '/receivables', icon: Landmark },
  { feature: 'delivery-performance', name: 'Delivery Performance', href: '/delivery-performance', icon: Bike },
  { feature: 'z-report', name: 'Z-Report', href: '/z-report', icon: ScrollText },
  { feature: 'expenses', name: 'Expenses', href: '/expenses', icon: Receipt },
  {
    feature: 'inventory',
    name: 'Inventory',
    icon: Warehouse,
    children: [
      { feature: 'purchase-report', name: 'Purchase Report', href: '/purchase-report', icon: ShoppingCart },
      { feature: 'profit-report', name: 'Profit Report', href: '/profit-report', icon: ChefHat },
      { feature: 'indent-reports', name: 'Indent Reports', href: '/indent-reports', icon: ClipboardList },
      { feature: 'pnl-report', name: 'P&L Report', href: '/pnl-report', icon: PieChart },
    ]
  },
  { feature: 'subscription', name: 'Subscription', href: '/subscription', icon: CreditCard },
]

// Shown instead of restaurantNavigation when a Head of Restaurant owner has
// selected the "All Restaurants" tab — a small, deliberately limited menu
// scoped to what's aggregated across restaurants today.
const combinedNavigation: NavItem[] = [
  { name: 'Combined Dashboard', href: '/combined/dashboard', icon: BarChart3 },
  { name: 'Combined Sales Report', href: '/combined/sales-report', icon: FileText },
]

export default function RestaurantLayout({ children }: RestaurantLayoutProps) {
  const { user, logout, subscriptionStatus } = useAuth()
  const { viewMode, currentRestaurant } = useRestaurant()
  const { theme, actualTheme, setTheme } = useTheme()
  const logo = actualTheme === 'dark' ? logoDark : logoLight
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())

  const navigation = viewMode === 'combined'
    ? combinedNavigation
    : restaurantNavigation
        .filter(item => !item.feature || isFeatureEnabled(currentRestaurant, item.feature))
        .map(item => item.children
          ? { ...item, children: item.children.filter(child => isFeatureEnabled(currentRestaurant, child.feature)) }
          : item)
        // An Inventory group with every sub-item switched off would be an empty, dead menu.
        .filter(item => !item.children || item.children.length > 0)

  const toggleMenu = (name: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  // Auto-expand parent when a child route is active
  useEffect(() => {
    navigation.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => location.pathname === child.href)
        if (hasActiveChild) {
          setExpandedMenus(prev => {
            if (prev.has(item.name)) return prev
            return new Set(prev).add(item.name)
          })
        }
      }
    })
  }, [location.pathname, navigation])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-4 w-4" />
    if (theme === 'dark') return <Moon className="h-4 w-4" />
    return <Monitor className="h-4 w-4" />
  }

  const getSubscriptionBadge = () => {
    if (subscriptionStatus === 'active') {
      return <Badge variant="success" className="text-xs">Active</Badge>
    } else if (subscriptionStatus === 'expired') {
      return <Badge variant="destructive" className="text-xs">Expired</Badge>
    } else if (subscriptionStatus === 'suspended') {
      return <Badge variant="warning" className="text-xs">Suspended</Badge>
    }
    return null
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r transition-transform duration-200 ease-in-out md:translate-x-0",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-6 border-b">
            <div className="flex items-center space-x-2">
              <img src={logo} alt="Ordore Logo" className="!h-[26px] w-auto" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Subscription Status */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Subscription</span>
              {getSubscriptionBadge()}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto thin-scrollbar space-y-1 p-4">
            {navigation.map((item) => {
              const Icon = item.icon

              // Collapsible parent with children
              if (item.children) {
                const isExpanded = expandedMenus.has(item.name)
                const hasActiveChild = item.children.some(child => location.pathname === child.href)

                return (
                  <div key={item.name}>
                    <Button
                      variant={hasActiveChild ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start space-x-2",
                        hasActiveChild && "bg-restaurant-primary/10 text-restaurant-primary hover:bg-restaurant-primary/20"
                      )}
                      onClick={() => toggleMenu(item.name)}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1 text-left">{item.name}</span>
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </Button>
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon
                          const isChildActive = location.pathname === child.href

                          return (
                            <Button
                              key={child.name}
                              variant={isChildActive ? "secondary" : "ghost"}
                              size="sm"
                              className={cn(
                                "w-full justify-start space-x-2",
                                isChildActive && "bg-restaurant-primary/10 text-restaurant-primary hover:bg-restaurant-primary/20"
                              )}
                              onClick={() => {
                                navigate(child.href)
                                setIsSidebarOpen(false)
                              }}
                            >
                              <ChildIcon className="h-3.5 w-3.5" />
                              <span>{child.name}</span>
                            </Button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              }

              // Regular nav item
              const isActive = location.pathname === item.href

              return (
                <Button
                  key={item.name}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start space-x-2",
                    isActive && "bg-restaurant-primary/10 text-restaurant-primary hover:bg-restaurant-primary/20"
                  )}
                  onClick={() => {
                    navigate(item.href!)
                    setIsSidebarOpen(false)
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{item.name}</span>
                  {item.isNew && (
                    <Badge className="bg-restaurant-primary text-white hover:bg-restaurant-primary text-[10px] px-1.5 py-0 leading-4">
                      New
                    </Badge>
                  )}
                </Button>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t space-y-2">
            <div className="flex items-center space-x-2 px-2 py-1">
              <div className="w-8 h-8 rounded-full bg-restaurant-primary/10 flex items-center justify-center">
                <img src={logoSvg} alt="Ordore Logo" className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">Restaurant Owner</p>
              </div>
            </div>
            <div className="flex space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="flex-1"
              >
                {getThemeIcon()}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex-1"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex h-16 items-center justify-between gap-2 border-b bg-card/80 backdrop-blur-sm px-4 md:px-6">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden flex-shrink-0"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>

            <RestaurantSwitcher />

            {/* <div className="hidden lg:block">
              <h1 className="text-lg font-semibold capitalize">
                {location.pathname.split('/').pop()?.replace('-', ' ')}.
              </h1>
            </div> */}
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            <UserMenu />
          </div>
        </div>

        {/* Page content */}
        <main className="p-3 sm:p-4 md:p-6 pb-20 md:pb-6">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav isSidebarOpen={isSidebarOpen} />
    </div>
  )
}